import { toNumber } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { getLinkCountsByFunctionAction } from 'store/actions/employee-function-link-actions';
import {
  EMPLOYEE_FUNCTION_LINKS__DELETE_FUNCTIONS_WARNING,
  EMPLOYEE_FUNCTION_LINKS__DELETE_FUNCTION_WARNING,
  GENERAL__DELETE_MESSAGE,
} from 'translations/constants';
import IPayrollFunction from 'types/company/payroll/IPayrollFunction';

interface PendingDeletion {
  functionIds: number[];
  counts: Record<number, number>;
}

export const useFunctionDeleteConfirmation = (functions: IPayrollFunction[]) => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const [pending, setPending] = useState<PendingDeletion>();

  const requestDelete = useCallback(
    async (functionIds: number[]) => {
      const counts = await dispatch(
        getLinkCountsByFunctionAction({ companyId: toNumber(companyId), functionIds }),
      ).unwrap();
      setPending({ functionIds, counts });
    },
    [companyId, dispatch],
  );

  const close = useCallback(() => setPending(undefined), []);

  const message = useMemo(() => {
    if (!pending) return '';

    const linkedIds = pending.functionIds.filter(id => (pending.counts[id] ?? 0) > 0);
    const employeeCount = linkedIds.reduce((total, id) => total + pending.counts[id], 0);

    if (linkedIds.length === 0) return t(GENERAL__DELETE_MESSAGE);
    if (linkedIds.length === 1) return t(EMPLOYEE_FUNCTION_LINKS__DELETE_FUNCTION_WARNING, { count: employeeCount });

    return t(EMPLOYEE_FUNCTION_LINKS__DELETE_FUNCTIONS_WARNING, {
      count: employeeCount,
      functions: linkedIds.map(id => functions.find(f => f.id === id)?.description ?? id).join(', '),
    });
  }, [pending, functions, t]);

  return { open: !!pending, message, requestDelete, close };
};

export default useFunctionDeleteConfirmation;
